import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { Spinner } from '@/components/ui/spinner';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import type { ToolSettings } from '@/shared/types/toolSettings';

import useMcpServers, { type McpTool } from '../hooks/useMcpServers';
import { getAuthHeaders } from '../ResyncDialog';
import { toMcpTool } from '../utils';
import CallErrorAlert from './CallErrorAlert';
import ToolOverviewPanel from './ToolOverviewPanel';
import ToolsTable, { type SortColumn, type ToolsSort } from './ToolsTable';

const sortRows = <T extends { name: string; enabled: boolean }>(
  rows: T[],
  sort: ToolsSort
): T[] => {
  if (!sort) {
    return rows;
  }
  const direction = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.column === 'name') {
      return a.name.localeCompare(b.name) * direction;
    }
    return (Number(a.enabled) - Number(b.enabled)) * direction;
  });
};

const McpServerPage = () => {
  useSegments(PageName.MCP_SERVER);
  const { t } = useTranslation();
  const { mcpId } = useParams<{ mcpId: string }>();
  const brainId = useSelector(selectBrainId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const {
    mcpServer: server,
    getStatus,
    updateMcpServer,
    updateStatus,
    testMutation,
    callToolMutation,
  } = useMcpServers(brainId ?? undefined, mcpId);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ToolsSort>(null);
  const [selectedName, setSelectedName] = useState<string>();
  const [liveTools, setLiveTools] = useState<McpTool[] | null>(null);
  const [toolsError, setToolsError] = useState<string | null>(null);

  // Switching to a different server drops the previous one's live list immediately — it must
  // never bleed into the next server's table while the fresh fetch is in flight.
  useEffect(() => {
    setLiveTools(null);
    setToolsError(null);
  }, [mcpId]);

  // Live-fetch the tools the server currently exposes, the same way the edit panel does.
  // `updated` re-runs it after any write to the server row — a re-sync or an edit changes what
  // the server exposes (or where), and without it the stale live list would keep winning the
  // merge below until a hard reload. The result lands in `liveTools` instead of being read off
  // `testMutation.data`, which resets to undefined while a refetch is in flight and would flash
  // the table back to the saved tools on every toggle.
  useEffect(() => {
    if (!server) {
      return;
    }
    testMutation.mutate(
      { url: server.url, headers: getAuthHeaders(server) },
      {
        onSuccess: (result) => {
          setLiveTools((result.tools ?? []).map(toMcpTool));
          setToolsError(null);
        },
        onError: (error) => setToolsError(error.message),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.mcp_id, server?.updated]);

  const isLoadingTools = liveTools === null && toolsError === null;

  // Live entries win — their schema is fresher — with saved-only tools kept as leftovers so an
  // unreachable server still lists what the agent has.
  const allTools = useMemo<McpTool[]>(() => {
    const live = liveTools ?? [];
    const liveNames = new Set(live.map((tool) => tool.name));
    const saved = server?.tools ?? [];
    return [...live, ...saved.filter((tool) => !liveNames.has(tool.name))];
  }, [liveTools, server?.tools]);

  const rows = useMemo(() => {
    const enabledNames = new Set((server?.tools ?? []).map((tool) => tool.name));
    const liveNames = new Set((liveTools ?? []).map((tool) => tool.name));
    const liveFetchFailed = Boolean(toolsError);
    return allTools.map((tool) => ({
      name: tool.name,
      enabled: enabledNames.has(tool.name),
      // While the server is unreachable, a saved-only tool has no other data source: disabling
      // it would delete the saved copy with no switch left to undo until the server is back.
      toggleLocked: liveFetchFailed && !liveNames.has(tool.name),
    }));
  }, [allTools, liveTools, server?.tools, toolsError]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query ? rows.filter((row) => row.name.toLowerCase().includes(query)) : rows;
    return sortRows(filtered, sort);
  }, [rows, search, sort]);

  const handleSort = useCallback((column: SortColumn) => {
    // asc → desc → back to the order the server reported.
    setSort((current) => {
      if (current?.column !== column) {
        return { column, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return null;
    });
  }, []);

  // Each save sends a whole field and the cache takes each response as it lands, so a write
  // sent before the previous one returns could be overwritten by it. Inputs stay enabled, which
  // keeps keyboard focus on them, and a change made during a save is dropped.
  const isSaving = updateStatus === 'pending';

  const handleToggle = useCallback(
    (name: string, enabled: boolean) => {
      if (!server || isSaving) {
        return;
      }
      if (!enabled) {
        updateMcpServer({
          brain_id: server.brain_id,
          mcp_id: server.mcp_id,
          tools: server.tools.filter((tool) => tool.name !== name),
        });
        return;
      }
      const tool = allTools.find((entry) => entry.name === name);
      if (!tool) {
        return;
      }
      updateMcpServer({
        brain_id: server.brain_id,
        mcp_id: server.mcp_id,
        tools: [...server.tools, tool],
      });
    },
    [allTools, isSaving, server, updateMcpServer]
  );

  const handleChangeSettings = useCallback(
    (name: string, settings: ToolSettings) => {
      if (!server || isSaving) {
        return;
      }
      updateMcpServer({
        brain_id: server.brain_id,
        mcp_id: server.mcp_id,
        tool_settings: { ...server.tool_settings, [name]: settings },
      });
    },
    [isSaving, server, updateMcpServer]
  );

  if (!server) {
    // A settled query with no server means the link is stale — the server was deleted or the id
    // is wrong. Without this branch the page would spin forever.
    if (getStatus === 'error') {
      return (
        <div className="flex flex-1 items-center justify-center p-6">
          <CallErrorAlert
            className="max-w-md"
            title={t('mcp.server_load_failed_title')}
            description={t('mcp.server_load_failed')}
          />
        </div>
      );
    }
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  // Derived rather than stored, so the selection heals itself when the row is filtered out.
  const selectedRow = visibleRows.find((row) => row.name === selectedName) ?? visibleRows[0];
  const selectedTool = isLoadingTools
    ? null
    : (allTools.find((tool) => tool.name === selectedRow?.name) ?? null);

  const emptyTableMessage = () => (search.trim() ? t('common.no_results') : t('mcp.no_tools'));

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center gap-1.5 p-6">
      <div className="flex w-full max-w-5xl items-center gap-6">
        <SearchBar
          size="sm"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('mcp.search_tools')}
        />
      </div>

      {/* Same alert the Test tab uses for a failed call, rather than a second red-block style. */}
      {toolsError && (
        <CallErrorAlert
          className="w-full max-w-5xl"
          title={t('mcp.tools_failed_title')}
          description={toolsError}
        />
      )}

      <div className="flex min-h-0 w-full max-w-5xl flex-1 gap-4 overflow-hidden rounded-xl bg-bg-muted p-1">
        <ToolsTable
          tools={visibleRows}
          selectedName={selectedRow?.name}
          canWrite={canWrite}
          emptyMessage={emptyTableMessage()}
          isLoading={isLoadingTools}
          sort={sort}
          onSelect={setSelectedName}
          onToggle={handleToggle}
          onSort={handleSort}
        />
        <ToolOverviewPanel
          tool={selectedTool}
          server={server}
          canWrite={canWrite}
          emptyMessage={t('mcp.select_tool')}
          isLoading={isLoadingTools}
          callToolMutation={callToolMutation}
          onChangeSettings={handleChangeSettings}
        />
      </div>
    </div>
  );
};

export default McpServerPage;
